import type { CallbackDataParams } from 'echarts/types/dist/shared';

import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import { TIER_COLOR, type WeekRisk } from '@/features/decisions/utils/stockoutRisk';
import { addDaysToIsoDate } from '@/utils/date';

// Continuous weekly dates from start..end inclusive, so weeks the forecast skips
// still render as empty buckets instead of collapsing the axis.
function weeklyDates(start: string, end: string): string[] {
  const out = [start];
  let cur = start;
  let guard = 0;
  while (cur < end && guard < 104) {
    cur = addDaysToIsoDate(cur, 7);
    out.push(cur);
    guard += 1;
  }
  return out;
}

// Weekly stockout-risk bar chart — the simplest form: one variable (stockout
// probability per week) over the protection-period weeks, plus the service
// target line. The reorder quantity that drives it lives in the controls bar.
export function buildStockoutRiskChartOption(
  weeks: WeekRisk[],
  policy: InventoryCoveragePolicy | undefined,
  decisionDate: string,
  targetRisk: number,
) {
  if (!policy || weeks.length === 0) {
    return {
      title: { text: 'No data', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
      xAxis: { show: false },
      yAxis: { show: false },
    };
  }

  // Continuous weekly axis from the decision date through the last risk week.
  // We fill the weekly grid (not just the weeks the forecast returned) and union
  // in the actual risk-week dates, so a forecast that skips a week renders an
  // empty bucket instead of collapsing the axis.
  const lastPeriod = weeks.length ? weeks[weeks.length - 1].period : decisionDate;
  const catSet = new Set<string>(weeklyDates(decisionDate, lastPeriod));
  weeks.forEach((w) => catSet.add(w.period));
  const categories = Array.from(catSet).sort();

  const riskByDate = new Map(weeks.map((w) => [w.period, w]));
  const targetPct = targetRisk * 100;

  // Bar values as percent; null where there's no protection-period risk for that
  // category (today, and weeks past the protection period).
  const data = categories.map((c) => {
    const w = riskByDate.get(c);
    if (!w) return { value: null as number | null };
    return {
      value: +(w.stockoutRisk * 100).toFixed(2),
      itemStyle: { color: TIER_COLOR[w.tier], borderColor: TIER_COLOR[w.tier], borderWidth: 1 },
    };
  });

  // Headroom so the tallest bar and the target line both stay readable.
  const peakPct = Math.max(targetPct, ...weeks.map((w) => w.stockoutRisk * 100));
  const yMax = Math.ceil((peakPct * 1.25) / 5) * 5 || 5;

  // ①②③ markers (mirrors the coverage chart): Decision (today), Reorder
  // available (decision + lead time, where supply steps up), Next available
  // (protection-period end = when the next order arrives).
  const lastIdx = categories.length - 1;
  const firstReceiptWeek = weeks.find((w) => w.reorderLanded);
  const hasLeadGap = weeks.some((w) => !w.reorderLanded);
  const receiptIdx = hasLeadGap && firstReceiptWeek ? categories.indexOf(firstReceiptWeek.period) : -1;
  const markerDefs = [
    { idx: 0, note: 'Decision' },
    ...(receiptIdx > 0 && receiptIdx < lastIdx ? [{ idx: receiptIdx, note: 'Available' }] : []),
    { idx: lastIdx, note: 'Next available' },
  ].filter((m, i, a) => a.findIndex((x) => x.idx === m.idx) === i);
  const markerData = markerDefs.map((m, i) => ({ value: [m.idx, 0], label: { formatter: String(i + 1) } }));
  const markerNotes: Record<number, string> = {};
  markerDefs.forEach((m, i) => {
    markerNotes[m.idx] = `${i + 1}. ${m.note}`;
  });

  return {
    animation: false,
    grid: { top: 44, right: 56, bottom: 84, left: 64 },
    graphic: [
      { type: 'text', left: 64, top: 6, style: { text: 'TODAY', fill: '#94a3b8', fontWeight: 'bold', fontSize: 11 } },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: CallbackDataParams | CallbackDataParams[]) => {
        const arr = Array.isArray(params) ? params : [params];
        const p = arr.find((x) => x.seriesName !== '__markers') ?? arr[0];
        const date = String(p?.name ?? '');
        const idx = typeof p?.dataIndex === 'number' ? p.dataIndex : undefined;
        const note = idx != null ? markerNotes[idx] : undefined;
        const noteRow = note ? `<span style="color:#64748b">${note}</span>` : '';
        const w = riskByDate.get(date);
        if (!w) return [`<b>${date}</b>`, noteRow].filter(Boolean).join('<br/>');
        const supply = `${Math.round(w.supply).toLocaleString()} Sale UOM`;
        const shortUnits = w.expectedUnitsShort < 1 ? '<1' : Math.round(w.expectedUnitsShort).toLocaleString();
        return [
          `<b>${date}</b>`,
          noteRow,
          `Chance of any stockout by this week: <b>${(w.stockoutRisk * 100).toFixed(w.stockoutRisk < 0.1 ? 1 : 0)}%</b>`,
          `Expected units short: <b>~${shortUnits} Sale UOM</b>`,
          `Supply: ${supply}${w.reorderLanded ? ' (reorder landed)' : ''}`,
          w.source === 'estimated' ? '<span style="color:#94a3b8">estimated from forecast quantiles</span>' : '',
        ]
          .filter(Boolean)
          .join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: 'Week Starting Date',
      nameLocation: 'middle',
      nameGap: 56,
      nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#334155' },
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisTick: { show: false },
      // interval 0 forces every weekly bucket to label, so the continuous grid
      // reads as weekly even where the forecast skipped a week. margin clears the
      // ①②③ markers (on the axis) so dates render below them without overlap.
      axisLabel: { fontSize: 10, color: '#64748b', interval: 0, margin: 26 },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: yMax,
      name: 'Stockout Risk',
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: { fontSize: 11, fontWeight: 600, color: '#334155' },
      axisLabel: { fontSize: 10, color: '#64748b', formatter: (v: number) => `${v}%` },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      {
        name: 'Stockout risk',
        type: 'bar',
        barWidth: 36,
        data,
        label: {
          show: true,
          position: 'top',
          fontSize: 11,
          fontWeight: 'bold',
          color: '#334155',
          formatter: (p: CallbackDataParams) =>
            typeof p.value === 'number' ? `${p.value < 10 ? p.value.toFixed(1) : Math.round(p.value)}%` : '',
        },
        markLine: {
          silent: true,
          symbol: 'none',
          data: [
            {
              yAxis: targetPct,
              lineStyle: { color: '#475569', type: 'dashed', width: 1.5 },
              label: {
                show: true,
                position: 'insideEndTop',
                formatter: `Target ${targetPct < 10 ? targetPct.toFixed(targetPct < 1 ? 1 : 0) : Math.round(targetPct)}%`,
                color: '#475569',
                fontSize: 10,
                fontWeight: 'bold',
              },
            },
          ],
        },
      },
      {
        name: '__markers',
        type: 'scatter',
        data: markerData,
        symbol: 'circle',
        symbolSize: 32,
        itemStyle: { color: '#fff', borderColor: '#64748b', borderWidth: 1.5 },
        label: { show: true, color: '#64748b', fontSize: 22, fontWeight: 'bold' },
        silent: true,
        tooltip: { show: false },
        z: 5,
      },
    ],
  };
}
